import React from 'react';
import { Container, Row, Col, Table } from 'react-bootstrap';

const Documentation = () => {
    return (
        <Container className="mt-5">
            <h2>Frontend Documentation for the React Application</h2>

            <h3>Overview</h3>
            <p>This React application provides a user interface for analyzing web designs and inspecting JSON data for potential deceptive design patterns. It has three primary options:</p>
            <ul>
                <li><strong>Load JSON</strong>: Allows users to load collected JSON data from a Chrome extension, which includes URLs, actions, and image URLs. The user can generate or edit image properties and inspect for deceptive designs.</li>
                <li><strong>Load Results</strong>: Enables users to load previously inspected result files and view or edit them.</li>
                <li><strong>Load Images</strong>: Allows users to directly upload and inspect images for deceptive design detection.</li>
            </ul>
            <p>The application leverages <strong>D3.js</strong> for tree visualization and <strong>React Bootstrap</strong> for styling and component structure. The flow involves multiple steps like generating image properties, editing JSON data, and downloading reports.</p>

            <h3>1. Header Component (Header.js)</h3>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th>Key Libraries</th>
                        <th>Purpose</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>react-bootstrap</td>
                        <td>Provides the structure and styling for the navigation bar.</td>
                    </tr>
                </tbody>
            </Table>
            <p><strong>Functions and Purpose:</strong></p>
            <ul>
                <li><strong>Navbar & Nav</strong>: Constructs a navigation bar with options like Explore, Dashboard, Project Management, and placeholders like User Manual and Chatbot.</li>
                <li><strong>onTabChange</strong>: Triggers tab switching when the user clicks on different navigation links.</li>
            </ul>

            <h3>2. Explore Page (ExplorePage.js)</h3>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th>Key Libraries</th>
                        <th>Purpose</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>react-bootstrap</td>
                        <td>Provides structure and styling for buttons, containers, forms, and alerts.</td>
                    </tr>
                    <tr>
                        <td>useState</td>
                        <td>Manages the component's state for folders, loading messages, and JSON data.</td>
                    </tr>
                </tbody>
            </Table>
            <p><strong>Functions and Purpose:</strong></p>
            <ul>
                <li><strong>handleLoadJson</strong>: Fetches folders containing JSON data from the server.</li>
                <li><strong>handleLoadResults</strong>: Fetches folders containing result files.</li>
                <li><strong>handleUploadImages</strong>: Allows users to upload multiple images for inspection.</li>
                <li><strong>handleSubmit</strong>: Submits selected JSON data or results from a folder for processing.</li>
                <li><strong>handleUploadSubmit</strong>: Uploads user-selected images and generates insights.</li>
            </ul>

            <h3>3. JSON Viewer (JsonViewer.js)</h3>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th>Key Libraries</th>
                        <th>Purpose</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>react-d3-tree</td>
                        <td>Used for rendering a tree visualization of JSON data.</td>
                    </tr>
                    <tr>
                        <td>useState</td>
                        <td>Manages the state for selected nodes in the JSON tree.</td>
                    </tr>
                </tbody>
            </Table>
            <p><strong>Functions and Purpose:</strong></p>
            <ul>
                <li><strong>Tree Visualization</strong>: Displays JSON data in a horizontal tree structure.</li>
                <li><strong>handleClick</strong>: Handles node selection in the tree and displays image properties when a leaf node (without children) is clicked.</li>
                <li><strong>renderNodeLabel</strong>: Renders the label of each tree node dynamically.</li>
            </ul>

            <h3>4. JSON Editor (JsonEditor.js)</h3>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th>Key Libraries</th>
                        <th>Purpose</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>react-d3-tree</td>
                        <td>Used for rendering a tree structure of the JSON data.</td>
                    </tr>
                    <tr>
                        <td>uuid</td>
                        <td>Generates unique identifiers for saving JSON data.</td>
                    </tr>
                    <tr>
                        <td>react-bootstrap</td>
                        <td>Provides structure and styling for forms, dropdowns, buttons, and modals.</td>
                    </tr>
                </tbody>
            </Table>
            <p><strong>Functions and Purpose:</strong></p>
            <ul>
                <li><strong>handleSaveNode</strong>: Saves changes made to a selected node's image properties.</li>
                <li><strong>handleSaveAll</strong>: Saves all changes and downloads the updated JSON file.</li>
                <li><strong>handleAgentSelect</strong>: Allows users to select agents for inspecting deceptive designs.</li>
                <li><strong>handleGenerateAgentResponses</strong>: Sends selected agents and image data to the backend to generate responses for deceptive design patterns.</li>
                <li><strong>handleRegenerateDescription & handleRegenerateValidation</strong>: Regenerates descriptions and validation data for images using backend APIs.</li>
            </ul>

            <h3>5. Report Page (ReportPage.js)</h3>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th>Key Libraries</th>
                        <th>Purpose</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>react-bootstrap</td>
                        <td>Used for rendering tables, buttons, and modals.</td>
                    </tr>
                    <tr>
                        <td>useLocation</td>
                        <td>Accesses the state passed to the report page (usually the JSON data to display).</td>
                    </tr>
                </tbody>
            </Table>
            <p><strong>Functions and Purpose:</strong></p>
            <ul>
                <li><strong>handleImageClick</strong>: Opens a modal with an enlarged view of an image when clicked.</li>
                <li><strong>downloadCSV</strong>: Converts JSON data into a CSV format and initiates a download.</li>
            </ul>

            <h3>Main Libraries & Why They're Used</h3>
            <ul>
                <li><strong>React Bootstrap</strong>: Provides pre-built components like buttons, forms, tables, and modals with a modern, responsive design.</li>
                <li><strong>react-d3-tree</strong>: Used to visualize the JSON structure in a hierarchical tree format.</li>
                <li><strong>uuid</strong>: Generates unique IDs for nodes and processes.</li>
                <li><strong>useState & useEffect</strong>: Core React hooks for managing state and side effects.</li>
            </ul>

            <h2>Backend Documentation for the Flask Application</h2>

            <h3>Overview of Libraries Used</h3>
            <Table striped bordered>
                <thead>
                    <tr>
                        <th>Library</th>
                        <th>Purpose</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Flask</td>
                        <td>Handles HTTP requests, routing, and responses for the backend API.</td>
                    </tr>
                    <tr>
                        <td>flask_cors</td>
                        <td>Enables CORS to allow requests from the frontend to the backend.</td>
                    </tr>
                    <tr>
                        <td>Google Cloud Storage</td>
                        <td>Used for storing and retrieving JSON files and images.</td>
                    </tr>
                    <tr>
                        <td>uuid</td>
                        <td>Generates unique IDs for files and processes.</td>
                    </tr>
                    <tr>
                        <td>csv</td>
                        <td>Handles CSV file operations.</td>
                    </tr>
                    <tr>
                        <td>requests</td>
                        <td>Makes HTTP requests to external services like Google Sheets.</td>
                    </tr>
                </tbody>
            </Table>

            <h3>Key Endpoints</h3>

            <h4>1. List Folders in Bucket</h4>
            <pre>
                <code>
                @app.route('/api/list-folders', methods=['GET'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> None</p>
            <p><strong>Returns:</strong> JSON list of folder names from the `chrome-extension-data` bucket.</p>

            <h4>2. List Results Folders</h4>
            <pre>
                <code>
                @app.route('/api/list-results-folders', methods=['GET'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> None</p>
            <p><strong>Returns:</strong> JSON list of folder names from the `dd-saved-results` bucket.</p>

            <h4>3. Load JSON Data</h4>
            <pre>
                <code>
                @app.route('/api/load-json', methods=['POST'])
                @app.route('/api/load-results-json', methods=['POST'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> A folder name in the request body.</p>
            <p><strong>Returns:</strong> JSON data from the selected folder.</p>

            <h4>4. Save JSON Data</h4>
            <pre>
                <code>
                @app.route('/api/save-json', methods=['POST'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> Updated JSON data in the request body.</p>
            <p><strong>Returns:</strong> Success message after saving the JSON to GCS.</p>

            <h4>5. Upload Images and Process</h4>
            <pre>
                <code>
                @app.route('/api/generate-for-images', methods=['POST'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> Images and a session name in the request body.</p>
            <p><strong>Returns:</strong> Processed image data and results.</p>

            <h4>6. Regenerate Description</h4>
            <pre>
                <code>
                @app.route('/api/regenerate-description', methods=['POST'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> Image URL in the request body.</p>
            <p><strong>Returns:</strong> Newly generated description for the image.</p>

            <h4>7. Regenerate Validation</h4>
            <pre>
                <code>
                @app.route('/api/regenerate-validation', methods=['POST'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> Image URL and description in the request body.</p>
            <p><strong>Returns:</strong> Validation result for the image.</p>

            <h4>8. Generate Agent Responses</h4>
            <pre>
                <code>
                @app.route('/api/run-agent', methods=['POST'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> Agent keys, image URL, and description in the request body.</p>
            <p><strong>Returns:</strong> Responses from the selected agents for the image.</p>

            <h4>9. Generate Suggestions</h4>
            <pre>
                <code>
                @app.route('/api/generate-suggestions', methods=['POST'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> Agent results and image description in the request body.</p>
            <p><strong>Returns:</strong> Suggestions based on the agent analysis.</p>

            <h4>10. Generate Feedback Image</h4>
            <pre>
                <code>
                @app.route('/api/generate-feedback-image', methods=['POST'])
                </code>
            </pre>
            <p><strong>Accepts:</strong> Suggestions and image description in the request body.</p>
            <p><strong>Returns:</strong> Feedback image generated based on the analysis.</p>

            <h3>Key Environmental Variables</h3>
            <ul>
                <li><strong>GOOGLE_APPLICATION_CREDENTIALS</strong>: Path to Google Cloud credentials.</li>
                <li><strong>bucket_name</strong>: The GCS bucket where JSON data is stored.</li>
                <li><strong>saved_bucket_name</strong>: The GCS bucket for result files.</li>
            </ul>
        </Container>
    );
};

export default Documentation;
